import { useState, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { CitizenHeader } from "@/components/CitizenHeader";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { MessageSquareWarning, Send, CheckCircle2, AlertTriangle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["water", "road", "drainage", "streetlight", "sanitation", "encroachment", "welfare", "tax", "certificate", "other"];

export default function GrievanceNew() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [panchayats, setPanchayats] = useState([]);
  const [form, setForm] = useState({
    panchayat_id: "", citizen_name: "", mobile: "", email: "",
    category: "water", subject: "", description: "", location: "",
  });
  const [busy, setBusy] = useState(false);
  const [ticket, setTicket] = useState(null);

  useEffect(() => {
    api.get("/panchayats").then((r) => {
      setPanchayats(r.data);
      setForm((f) => ({ ...f, panchayat_id: r.data[0]?.id || "" }));
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/grievances", form);
      setTicket(r.data);
      toast.success("Grievance filed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (ticket) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <CitizenHeader />
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="rounded-md border border-emerald-200 bg-white p-10 text-center shadow-sm" data-testid="griev-success">
            <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h2 className="mt-5 font-heading text-3xl font-bold text-slate-900">{lang === "hi" ? "शिकायत दर्ज हुई" : "Grievance Filed"}</h2>
            <p className="mt-2 text-slate-600">{lang === "hi" ? "आपकी टिकट संख्या" : "Your ticket number"}</p>
            <div className="mt-4 inline-block px-6 py-3 rounded-md border-2 border-[#0A3161] bg-[#0A3161]/5">
              <div className="font-mono font-bold text-2xl text-[#0A3161]">{ticket.ticket_no}</div>
            </div>
            <div className="mt-6 text-sm text-slate-600">
              SLA: <span className="font-semibold text-slate-900">{ticket.sla_days} {t("days")}</span>
            </div>
            <div className="mt-8 flex justify-center gap-3">
              <Link to={`/track?t=${ticket.ticket_no}`} className="px-4 py-2 text-sm font-semibold rounded-md text-white" style={{ background: "#0A3161" }} data-testid="track-after-file">{t("ctaTrack")}</Link>
              <Link to="/" className="px-4 py-2 text-sm rounded-md border border-slate-300">{t("home")}</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <CitizenHeader />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#0A3161] mb-4"><ArrowLeft className="w-4 h-4" /> {t("home")}</Link>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722] section-rule">{t("grievance")}</div>
        <h1 className="mt-3 font-heading text-4xl font-bold text-slate-900">{t("fileGrievance")}</h1>
        <p className="mt-2 text-slate-600">{t("grievanceSub")}</p>

        <form onSubmit={submit} className="mt-8 rounded-md border border-slate-200 bg-white p-7 space-y-5" data-testid="grievance-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t("fullName")} required>
              <input required value={form.citizen_name} onChange={(e) => setForm({ ...form, citizen_name: e.target.value })} className="form-input" data-testid="gr-name" />
            </Field>
            <Field label={t("mobile")} required>
              <input required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="form-input" data-testid="gr-mobile" />
            </Field>
            <Field label={t("panchayat")} required>
              <select required value={form.panchayat_id} onChange={(e) => setForm({ ...form, panchayat_id: e.target.value })} className="form-input" data-testid="gr-pan">
                {panchayats.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.district})</option>)}
              </select>
            </Field>
            <Field label={t("category")} required>
              <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="form-input" data-testid="gr-cat">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label={t("subject")} required>
                <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="form-input" data-testid="gr-subj" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label={t("description")} required>
                <textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="form-input" data-testid="gr-desc" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label={t("location")}>
                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="form-input" placeholder="Ward, locality, landmark" />
              </Field>
            </div>
          </div>
          <button type="submit" disabled={busy} data-testid="gr-submit" className="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-md text-white" style={{ background: "#E87722" }}>
            <Send className="w-4 h-4" /> {busy ? "…" : t("submit")}
          </button>
        </form>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          padding: 0.6rem 0.75rem;
          border-radius: 0.375rem;
          border: 1px solid #CBD5E1;
          background: white;
          font-size: 0.875rem;
          outline: none;
        }
        .form-input:focus { border-color: #0A3161; box-shadow: 0 0 0 3px rgba(10,49,97,0.08); }
      `}</style>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">
      {label}{required && <span className="text-[#E87722] ml-0.5">*</span>}
    </label>
    <div className="mt-1">{children}</div>
  </div>
);

export function GrievanceTrack() {
  const { t, lang } = useLang();
  const [ticket, setTicket] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    const t0 = new URLSearchParams(window.location.search).get("t");
    if (t0) {
      setTicket(t0);
      doTrack(t0);
    }
  }, []);

  const doTrack = async (val) => {
    setErr(""); setResult(null);
    try {
      const r = await api.get(`/grievances/track/${val || ticket}`);
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Not found");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <CitizenHeader />
      <div className="max-w-2xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#0A3161] mb-4"><ArrowLeft className="w-4 h-4" /> {t("home")}</Link>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722] section-rule">{t("track")}</div>
        <h1 className="mt-3 font-heading text-4xl font-bold text-slate-900">{t("trackTitle")}</h1>
        <p className="mt-2 text-slate-600">{t("trackSub")}</p>

        <div className="mt-8 rounded-md border border-slate-200 bg-white p-7">
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{t("ticketNo")}</label>
          <div className="mt-1.5 flex gap-2">
            <input value={ticket} onChange={(e) => setTicket(e.target.value)} placeholder="GRV/2026/0001"
              data-testid="track-input"
              className="flex-1 px-3 py-2.5 rounded-md border border-slate-300 bg-white text-sm font-mono outline-none focus:border-[#0A3161]" />
            <button onClick={() => doTrack()} data-testid="track-btn" className="px-4 py-2.5 text-sm font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>{t("track")}</button>
          </div>

          {err && <div className="mt-4 flex gap-2 items-center text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3"><AlertTriangle className="w-4 h-4" /> {err}</div>}

          {result && (
            <div className="mt-6 rounded-md border border-slate-200 p-5 space-y-3 text-sm" data-testid="track-result">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("ticketNo")}</div>
                  <div className="font-mono font-bold text-slate-900">{result.ticket_no}</div>
                </div>
                <span className={`px-3 py-1 text-[10px] uppercase tracking-wider rounded font-semibold ${
                  result.status === "resolved" ? "bg-emerald-50 text-emerald-700" :
                  result.status === "in_progress" ? "bg-amber-50 text-amber-700" :
                  result.status === "closed" ? "bg-slate-100 text-slate-700" :
                  "bg-red-50 text-red-700"
                }`}>{t(result.status)}</span>
              </div>
              <div className="border-t border-slate-100 pt-3 space-y-2">
                <div><span className="text-slate-500">{t("subject")}: </span><span className="font-medium">{result.subject}</span></div>
                <div><span className="text-slate-500">{t("category")}: </span>{result.category}</div>
                <div><span className="text-slate-500">SLA: </span>{result.sla_days} {t("days")}</div>
                <div><span className="text-slate-500">Filed: </span>{new Date(result.created_at).toLocaleString("en-IN")}</div>
                {result.resolution_notes && (
                  <div className="pt-2 border-t border-slate-100">
                    <div className="text-slate-500 text-xs">Resolution notes</div>
                    <div className="mt-1">{result.resolution_notes}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
